import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AccountSummary,
  type ImportAccountSessionResponse,
  type ProxySummary,
  Routes,
  type StartAccountLoginResponse,
  type SubmitLoginCodeResponse,
  type SubmitTwoFaPasswordResponse,
} from '@tg-leads/contracts';

import { AccountDetailDrawer } from '../../components/AccountDetailDrawer';
import { api } from '../../lib/api';
import { friendlyError } from '../../lib/friendlyError';

interface AccountList {
  items: AccountSummary[];
  nextCursor: string | null;
}
interface ProxyList {
  items: ProxySummary[];
  nextCursor: string | null;
}

const statusBadge = (s: AccountSummary['status']) => {
  switch (s) {
    case 'active':
      return 'badge-ok';
    case 'restricted':
      return 'badge-warn';
    case 'cooling':
      return 'badge-warn';
    case 'banned':
      return 'badge-fail';
    default:
      return 'badge-neutral';
  }
};

const statusLabel = (s: AccountSummary['status']): string => {
  switch (s) {
    case 'active':
      return '启用中';
    case 'restricted':
      return '受限';
    case 'cooling':
      return '冷却中';
    case 'banned':
      return '已封禁';
    default:
      return s;
  }
};

const purposeLabel = (p: AccountSummary['purpose']): string =>
  p === 'sender' ? '外联号' : '采集号';

const warmupLabel = (w: AccountSummary['warmupPhase']): string => {
  switch (w) {
    case 'cold':
      return '冷启动';
    case 'warming':
      return '养号中';
    case 'ready':
      return '就绪';
    default:
      return w;
  }
};

export function AccountsPage() {
  const qc = useQueryClient();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountList>(Routes.admin.accounts),
  });

  // Proxies fetched alongside so the table can show "1.2.3.4:1080" instead
  // of an opaque UUID slice. Joined client-side because /admin/proxies is
  // small and already cached for the wizard.
  const proxies = useQuery({
    queryKey: ['proxies'],
    queryFn: () => api.get<ProxyList>(Routes.admin.proxies),
  });
  const proxyEndpoint = (id: string | null): string => {
    if (!id) return '—';
    const p = proxies.data?.items.find((x) => x.id === id);
    return p ? p.endpoint : id.slice(0, 8);
  };

  const selected = selectedId
    ? accounts.data?.items.find((a) => a.id === selectedId) ?? null
    : null;

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(Routes.admin.account(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<{ account: AccountSummary }>(Routes.admin.accountPause(id), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const resumeMutation = useMutation({
    mutationFn: (id: string) =>
      api.post<{ account: AccountSummary }>(Routes.admin.accountResume(id), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const [pendingDelete, setPendingDelete] = useState<AccountSummary | null>(
    null,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">TG 账号</h1>
          <p className="text-sm text-slate-500 mt-1">
            管理用来连 Telegram 的账号。会话凭据与两步验证密码全程 KMS 加密入库，
            浏览器只在登录流程里临时缓存验证码。
          </p>
        </div>
        <button className="btn-primary" onClick={() => setWizardOpen(true)}>
          + 添加 TG 账号
        </button>
      </div>

      {wizardOpen && (
        <AddAccountWizard
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['accounts'] });
            qc.invalidateQueries({ queryKey: ['proxies'] });
            setWizardOpen(false);
          }}
          onClose={() => setWizardOpen(false)}
        />
      )}

      <div className="card overflow-x-auto">
        {accounts.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : accounts.error ? (
          <div className="text-red-600 text-sm">
            {(accounts.error as Error).message}
          </div>
        ) : !accounts.data?.items.length ? (
          <div className="text-slate-500 text-sm">
            还没有 TG 账号。点右上角"添加 TG 账号"开始。
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4">手机号</th>
                <th className="py-2 pr-4">TG 显示名</th>
                <th className="py-2 pr-4">TG 用户名</th>
                <th className="py-2 pr-4">用途</th>
                <th className="py-2 pr-4">状态</th>
                <th className="py-2 pr-4">连接</th>
                <th className="py-2 pr-4">养号阶段</th>
                <th className="py-2 pr-4">健康分</th>
                <th className="py-2 pr-4">今日已发私信</th>
                <th className="py-2 pr-4">代理</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {accounts.data.items.map((a) => (
                <tr
                  key={a.id}
                  className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedId(a.id)}
                >
                  <td className="py-3 pr-4 font-mono">{a.phone}</td>
                  <td className="py-3 pr-4">{displayName(a) || '—'}</td>
                  <td className="py-3 pr-4 text-indigo-700">
                    {a.tgUsername ? `@${a.tgUsername}` : '—'}
                  </td>
                  <td className="py-3 pr-4">
                    <span
                      className={
                        a.purpose === 'sender' ? 'badge-warn' : 'badge-neutral'
                      }
                      title={
                        a.purpose === 'sender'
                          ? '只发私信，不会加群'
                          : '只采集消息，不会主动发私信'
                      }
                    >
                      {purposeLabel(a.purpose)}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className={statusBadge(a.status)}>{statusLabel(a.status)}</span>
                  </td>
                  <td className="py-3 pr-4">
                    {a.adminPaused ? (
                      <span
                        className="badge-warn"
                        title="管理员手动下线 — 不会自动重连，需要点'上线'恢复"
                      >
                        已暂停
                      </span>
                    ) : (
                      <span
                        className={a.isConnected ? 'badge-ok' : 'badge-fail'}
                        title={
                          a.isConnected
                            ? 'mtcute 客户端已连上 TG（本地 handle 在线）'
                            : '未连接 / 已掉线（下次 reconcile 会重试）'
                        }
                      >
                        {a.isConnected ? '在线' : '离线'}
                      </span>
                    )}
                    {a.discoveryCooldownUntil && (
                      <span
                        className="badge-warn ml-1"
                        title={`被 TG 限流，资源发现对此号暂停至 ${new Date(a.discoveryCooldownUntil).toLocaleString()}；监听群消息不受影响`}
                      >
                        限流冷却
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <span className="badge-neutral">{warmupLabel(a.warmupPhase)}</span>
                  </td>
                  <td className="py-3 pr-4">{a.healthScore}</td>
                  <td className="py-3 pr-4">{a.dailyDmCount}</td>
                  <td className="py-3 pr-4 text-xs font-mono">
                    {proxyEndpoint(a.proxyId)}
                  </td>
                  <td className="py-3 pr-4 space-x-2 whitespace-nowrap">
                    {a.adminPaused ? (
                      <button
                        className="btn-secondary"
                        disabled={resumeMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          resumeMutation.mutate(a.id);
                        }}
                        title="清除暂停标记，下次 reconcile（≤30s）自动重新连接"
                      >
                        上线
                      </button>
                    ) : (
                      <button
                        className="btn-secondary"
                        disabled={pauseMutation.isPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          pauseMutation.mutate(a.id);
                        }}
                        title="立刻 evict mtcute handle 并标记暂停，不会自动重连"
                      >
                        下线
                      </button>
                    )}
                    <button
                      className="btn-danger"
                      disabled={a.status === 'active' || deleteMutation.isPending}
                      title={
                        a.status === 'active'
                          ? '账号是"启用中"状态时不能删除，先冷却 / 封禁'
                          : ''
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(a);
                      }}
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pendingDelete && (
        <DeleteAccountDialog
          account={pendingDelete}
          onClose={() => setPendingDelete(null)}
          onConfirm={() => {
            deleteMutation.mutate(pendingDelete.id);
            setPendingDelete(null);
          }}
        />
      )}

      {selected && (
        <AccountDetailDrawer
          account={selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function displayName(a: AccountSummary): string {
  const parts = [a.tgFirstName, a.tgLastName].filter(Boolean).join(' ');
  return parts;
}

/**
 * Normalize whatever the operator pasted into a +E.164 phone string.
 * Strips spaces / dashes / parens / leading + (then re-adds + once) so
 * paste-friendly inputs like "86 150-1234-5678" or "8615012345678" all
 * become "+8615012345678". Empty input → empty string (caller validates).
 */
function normalizePhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
}

/**
 * Account purpose selector — strict observer/sender split.
 * Defaulted to observer because the typical "first account I add" is for
 * group collection; sender accounts are added later after warm-up.
 */
function PurposeSelector({
  value,
  onChange,
}: {
  value: 'observer' | 'sender';
  onChange: (v: 'observer' | 'sender') => void;
}) {
  return (
    <div>
      <label className="label">账号用途（提交后不可切换，请慎选）</label>
      <div className="flex gap-3">
        <label
          className={`flex-1 cursor-pointer border rounded-lg p-3 ${
            value === 'observer'
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-slate-300 bg-white'
          }`}
        >
          <input
            type="radio"
            name="purpose"
            value="observer"
            checked={value === 'observer'}
            onChange={() => onChange('observer')}
            className="mr-2"
          />
          <span className="text-sm font-medium">采集号</span>
          <p className="text-xs text-slate-500 mt-1">
            加群、收消息送进管线。永远不会主动给陌生人发私信。
            适合"潜伏号"，可多加群、风险低。
          </p>
        </label>
        <label
          className={`flex-1 cursor-pointer border rounded-lg p-3 ${
            value === 'sender'
              ? 'border-indigo-500 bg-indigo-50'
              : 'border-slate-300 bg-white'
          }`}
        >
          <input
            type="radio"
            name="purpose"
            value="sender"
            checked={value === 'sender'}
            onChange={() => onChange('sender')}
            className="mr-2"
          />
          <span className="text-sm font-medium">外联号</span>
          <p className="text-xs text-slate-500 mt-1">
            由自动外联调度器挑选，主动给线索发私信。系统会拒绝把这种号加进任何群。
            建议用干净、有头像有名字的号。
          </p>
        </label>
      </div>
    </div>
  );
}

type WizardMode = 'sms' | 'session';

function AddAccountWizard({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  // Default to session import — it's the path that works today (SMS flow
  // depends on Phase 5.1 real mtcute).
  const [mode, setMode] = useState<WizardMode>('session');

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card w-[480px] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">添加 TG 账号</h2>

        <div className="flex gap-1 p-1 bg-slate-100 rounded-md">
          <button
            type="button"
            className={`flex-1 py-1.5 text-sm rounded transition ${
              mode === 'session'
                ? 'bg-white text-indigo-700 shadow-sm font-medium'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => setMode('session')}
          >
            已有会话字符串
          </button>
          <button
            type="button"
            className={`flex-1 py-1.5 text-sm rounded transition ${
              mode === 'sms'
                ? 'bg-white text-indigo-700 shadow-sm font-medium'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => setMode('sms')}
          >
            手机号 + 短信验证码
          </button>
        </div>

        {mode === 'session' ? (
          <SessionImportForm onDone={onDone} onClose={onClose} />
        ) : (
          <SmsWizard onDone={onDone} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

// ---------------- session import path (Path B) ----------------

function SessionImportForm({
  onDone,
  onClose,
}: {
  onDone: () => void;
  onClose: () => void;
}) {
  const [phone, setPhone] = useState('');
  const [proxyId, setProxyId] = useState('');
  const [purpose, setPurpose] = useState<'observer' | 'sender'>('observer');
  const [sessionString, setSessionString] = useState('');
  const [twofaPassword, setTwofaPassword] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [imported, setImported] = useState<AccountSummary | null>(null);

  const proxies = useQuery({
    queryKey: ['proxies'],
    queryFn: () => api.get<ProxyList>(Routes.admin.proxies),
    // Sort by least-loaded first so operator picks a freer IP by default.
    select: (d) =>
      [...d.items].sort((a, b) => a.boundAccountCount - b.boundAccountCount),
  });

  const mut = useMutation({
    mutationFn: () =>
      api.post<ImportAccountSessionResponse>(
        Routes.admin.accountsImportSession,
        {
          phone: normalizePhone(phone),
          proxyId,
          purpose,
          sessionString: sessionString.trim(),
          twofaPassword: twofaPassword.trim() || undefined,
        },
      ),
    onSuccess: (data) => {
      setImported(data.account);
      setErr(null);
    },
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  if (imported) {
    return (
      <div className="space-y-4">
        <div className="text-green-700 bg-green-50 p-4 rounded">
          ✓ 账号 <span className="font-mono">{imported.phone}</span> 已导入。
        </div>
        <p className="text-xs text-slate-500">
          会话凭据已经 KMS 加密入库，养号阶段标记为"就绪"（导入的会话视为已养好）。
          一旦凭据被 TG 注销，下次连接时会检测到，账号自动转为"已封禁"。
        </p>
        <div className="flex justify-end">
          <button className="btn-primary" onClick={onDone}>
            完成
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setErr(null);
        mut.mutate();
      }}
      className="space-y-4"
    >
      <p className="text-xs text-slate-500 bg-slate-50 p-3 rounded">
        从其他工具或旧实例导出的 mtcute 会话字符串可直接粘贴在这里，跳过短信验证。
        字符串绝不会出现在任何列表里，提交后立即 KMS 加密入库。
      </p>
      <div>
        <label className="label">手机号（仅做账号匹配，不会再发短信）</label>
        <input
          className="input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="例如 8615012345678（粘贴时不用带 +，空格 / - 也会自动去掉）"
          required
        />
      </div>
      <div>
        <label className="label">绑定代理（按已绑账号数升序，越少越优先）</label>
        {proxies.isLoading ? (
          <div className="text-sm text-slate-500">加载代理列表…</div>
        ) : !proxies.data?.length ? (
          <div className="text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded">
            还没有代理。请先到"代理（SOCKS5）"页面添加一个。
          </div>
        ) : (
          <select
            className="input"
            value={proxyId}
            onChange={(e) => setProxyId(e.target.value)}
            required
          >
            <option value="">— 选择代理 —</option>
            {proxies.data.map((p) => (
              <option key={p.id} value={p.id}>
                {p.endpoint} {p.hasAuth ? '🔒' : ''} — 已绑 {p.boundAccountCount} 个
                {p.boundAccountCount >= 5 ? ' ⚠️' : ''}
              </option>
            ))}
          </select>
        )}
        <p className="text-xs text-slate-400 mt-1">
          ⚠️ 表示已绑 5 个及以上，建议同一住宅 IP 不超过 5 个账号以降低 TG 风控风险。
        </p>
      </div>
      <PurposeSelector value={purpose} onChange={setPurpose} />
      <div>
        <label className="label">会话字符串（mtcute StringSession）</label>
        <textarea
          className="input font-mono text-xs"
          rows={5}
          value={sessionString}
          onChange={(e) => setSessionString(e.target.value)}
          required
          autoComplete="off"
          spellCheck={false}
          placeholder="base64 编码的会话字符串…"
        />
      </div>
      <div>
        <label className="label">
          两步验证云密码 <span className="text-slate-400">（可选）</span>
        </label>
        <input
          type="password"
          className="input"
          value={twofaPassword}
          onChange={(e) => setTwofaPassword(e.target.value)}
          autoComplete="new-password"
          placeholder="账号开了两步验证时才需要填"
        />
      </div>
      {err && (
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
          {err}
        </div>
      )}
      <div className="flex justify-end space-x-2">
        <button type="button" className="btn-secondary" onClick={onClose}>
          取消
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={mut.isPending || !proxyId || !sessionString.trim() || !normalizePhone(phone)}
        >
          {mut.isPending ? '导入中…' : '导入并保存'}
        </button>
      </div>
    </form>
  );
}

// ---------------- SMS wizard path (Path A) ----------------

type SmsStep = 'pick' | 'code' | 'twoFa' | 'done';

function SmsWizard({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState<SmsStep>('pick');
  const [phone, setPhone] = useState('');
  const [proxyId, setProxyId] = useState('');
  const [purpose, setPurpose] = useState<'observer' | 'sender'>('observer');
  const [code, setCode] = useState('');
  const [pwd, setPwd] = useState('');
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [remaining, setRemaining] = useState(180);

  const proxies = useQuery({
    queryKey: ['proxies'],
    queryFn: () => api.get<ProxyList>(Routes.admin.proxies),
    select: (d) =>
      [...d.items].sort((a, b) => a.boundAccountCount - b.boundAccountCount),
    enabled: step === 'pick',
  });

  useEffect(() => {
    if (!expiresAt) return;
    const t = setInterval(() => {
      const r = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
      setRemaining(r);
      if (r === 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);

  const startMut = useMutation({
    mutationFn: () =>
      api.post<StartAccountLoginResponse>(Routes.admin.accountsLoginStart, {
        phone: normalizePhone(phone),
        proxyId,
        purpose,
        tenantId: '00000000-0000-0000-0000-000000000010', // SEED tenant; multi-tenant later
      }),
    onSuccess: (data) => {
      setAttemptId(data.loginAttemptId);
      setExpiresAt(new Date(data.expiresAt));
      setStep('code');
      setErr(null);
    },
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  const codeMut = useMutation({
    mutationFn: () =>
      api.post<SubmitLoginCodeResponse>(Routes.admin.accountsLoginCode, {
        loginAttemptId: attemptId,
        code,
      }),
    onSuccess: (data) => {
      if (data.status === 'need_2fa') {
        setStep('twoFa');
        setErr(null);
      } else {
        setStep('done');
      }
    },
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  const twoFaMut = useMutation({
    mutationFn: () =>
      api.post<SubmitTwoFaPasswordResponse>(Routes.admin.accountsLoginTwoFa, {
        loginAttemptId: attemptId,
        password: pwd,
      }),
    onSuccess: () => setStep('done'),
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  return (
    <div>
      <div className="text-xs text-slate-400 mb-3">
        步骤 {{ pick: '1/3 填手机号', code: '2/3 输验证码', twoFa: '3/3 输两步验证密码', done: '完成' }[step]}
      </div>
      {step === 'pick' && (
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setErr(null);
            startMut.mutate();
          }}
          className="space-y-4"
        >
          <p className="text-xs text-amber-700 bg-amber-50 p-3 rounded">
            ⚠️ 短信验证路径还在对接 mtcute，目前可能返回
            <code className="font-mono text-xs"> mtcute_factory_not_implemented</code>。
            如果已经有会话字符串，建议直接走上面的"已有会话字符串"路径。
          </p>
          <div>
            <label className="label">手机号（带国家区号）</label>
            <input
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+8615012345678"
              pattern="\+?\d{8,15}"
              required
            />
          </div>
          <div>
            <label className="label">绑定代理（按已绑账号数升序）</label>
            {proxies.isLoading ? (
              <div className="text-sm text-slate-500">加载代理列表…</div>
            ) : !proxies.data?.length ? (
              <div className="text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded">
                还没有代理。请先到"代理（SOCKS5）"页面添加一个。
              </div>
            ) : (
              <select
                className="input"
                value={proxyId}
                onChange={(e) => setProxyId(e.target.value)}
                required
              >
                <option value="">— 选择代理 —</option>
                {proxies.data.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.endpoint} {p.hasAuth ? '🔒' : ''} — 已绑 {p.boundAccountCount} 个
                    {p.boundAccountCount >= 5 ? ' ⚠️' : ''}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-slate-400 mt-1">
              账号一旦签发，运行期都走同一个代理；账号被删除后代理会自动释放。
            </p>
          </div>
          <PurposeSelector value={purpose} onChange={setPurpose} />
          {err && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {err}
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={startMut.isPending || !proxyId || !normalizePhone(phone)}
            >
              {startMut.isPending ? '正在发送验证码…' : '下一步：让 TG 发送短信验证码'}
            </button>
          </div>
        </form>
      )}

      {step === 'code' && (
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setErr(null);
            codeMut.mutate();
          }}
          className="space-y-4"
        >
          <div className="text-sm text-slate-600">
            验证码已发到 <span className="font-mono">{normalizePhone(phone)}</span>。
            <br />
            剩余时间：
            <span
              className={`font-mono ${
                remaining < 30 ? 'text-red-600' : 'text-slate-700'
              }`}
            >
              {Math.floor(remaining / 60)}:
              {String(remaining % 60).padStart(2, '0')}
            </span>
          </div>
          <div>
            <label className="label">5 位短信验证码</label>
            <input
              className="input font-mono text-lg tracking-widest text-center"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              maxLength={5}
              pattern="\d{5}"
              required
              autoFocus
            />
          </div>
          {err && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {err}
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={codeMut.isPending || code.length !== 5 || remaining === 0}
            >
              {codeMut.isPending ? '验证中…' : '提交验证码'}
            </button>
          </div>
        </form>
      )}

      {step === 'twoFa' && (
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setErr(null);
            twoFaMut.mutate();
          }}
          className="space-y-4"
        >
          <div className="text-sm text-slate-600">
            这个账号开启了两步验证，请输入云密码完成登录。
          </div>
          <div>
            <label className="label">两步验证云密码</label>
            <input
              type="password"
              className="input"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              required
              autoFocus
              autoComplete="new-password"
            />
          </div>
          {err && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
              {err}
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={twoFaMut.isPending || !pwd}
            >
              {twoFaMut.isPending ? '验证中…' : '完成登录'}
            </button>
          </div>
        </form>
      )}

      {step === 'done' && (
        <div className="space-y-4">
          <div className="text-green-700 bg-green-50 p-4 rounded">
            ✓ 账号已添加。会话凭据已 KMS 加密入库。
          </div>
          <p className="text-xs text-slate-500">
            默认养号阶段为"冷启动"，进入 7-30 天养号期；养好后可在账号详情手动改为"就绪"。
          </p>
          <div className="flex justify-end">
            <button className="btn-primary" onClick={onDone}>
              完成
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Destructive confirmation modal — operator must type the literal word
 * "DELETE" to enable the delete button. Pattern borrowed from Stripe /
 * Vercel / GitHub for irreversible operations.
 */
function DeleteAccountDialog({
  account,
  onClose,
  onConfirm,
}: {
  account: AccountSummary;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  const canDelete = typed === 'DELETE';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card w-[480px] space-y-4 border-2 border-red-200"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-red-700">
          确认删除 TG 账号
        </h2>
        <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded p-3 space-y-2">
          <div>
            即将删除：
            <span className="font-mono ml-1">{account.phone}</span>
            {account.tgUsername ? (
              <span className="text-slate-500 ml-2">@{account.tgUsername}</span>
            ) : null}
          </div>
          <ul className="list-disc pl-5 text-xs space-y-1">
            <li>会话凭据（KMS 加密的 mtcute session）会被彻底清除</li>
            <li>账号的群关联会断开（数据库里 joinedByAccountId 置空）</li>
            <li>历史消息 / 线索 / 会话保留（不级联删除）</li>
            <li>
              <strong>不可恢复</strong>。若要重新使用该号码，需要重新走"添加 TG 账号"流程
            </li>
          </ul>
        </div>
        <div>
          <label className="label">
            请输入 <span className="font-mono text-red-700">DELETE</span>{' '}
            确认（大小写敏感）
          </label>
          <input
            className="input font-mono"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
            autoFocus
          />
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={!canDelete}
            onClick={onConfirm}
          >
            删除账号
          </button>
        </div>
      </div>
    </div>
  );
}
